import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';

import classnames from 'classnames';
import _, { forEach } from 'lodash';
import { DateTime } from 'luxon';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { DatePickerWithLocale } from 'core/datepicker/DatePicker';
import { Overlay } from 'core/overlay/Overlay';
import { Select } from 'core/select/Select';
import { ButtonVariant } from 'core/util/Bootstrap';
import { convertCalendarDateToDateTime, convertDateTimeToCalendarDate } from 'social/common/util/Util';
import { CalendarDateObject, PublishType, SelectedSource } from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';

import 'social/publisher/optimal-times-picker/OptimalTimesPicker.scss';
import { fetchOptimalPublishTimes } from '../PublisherFetchers';
import { getSourceKeysFromSelectedSources } from '../util/Util';

interface Props {
    tenantId: number | undefined;
    publishType: PublishType;
    selectedSources: SelectedSource;
    sourceToPublishTime: { string: CalendarDateObject } | null | undefined;
}

const DayOfWeek: Record<string, string> = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
};

const MilitaryTime: Record<string, string> = {
    '0': '12:00 am',
    '1': '1:00 am',
    '2': '2:00 am',
    '3': '3:00 am',
    '4': '4:00 am',
    '5': '5:00 am',
    '6': '6:00 am',
    '7': '7:00 am',
    '8': '8:00 am',
    '9': '9:00 am',
    '10': '10:00 am',
    '11': '11:00 am',
    '12': '12:00 pm',
    '13': '1:00 pm',
    '14': '2:00 pm',
    '15': '3:00 pm',
    '16': '4:00 pm',
    '17': '5:00 pm',
    '18': '6:00 pm',
    '19': '7:00 pm',
    '20': '8:00 pm',
    '21': '9:00 pm',
    '22': '10:00 pm',
    '23': '11:00 pm',
};

const OVERLAY_AREA = 'optimal-times';

export const OptimalTimesPicker: FunctionComponent<Props> = ({
    tenantId,
    publishType,
    selectedSources,
    sourceToPublishTime,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);

    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [optimalTimes, setOptimalTimes] = useState<any[]>([]);
    const [selectedOptimalTimes, setSelectedOptimalTimes] = useState<Record<string, string>>({});

    const [isModalShowing, setIsModalShowing] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleShowModal = (): void => {
        setIsModalShowing(true);
    };

    const handleCloseModal = (): void => {
        setSelectedOptimalTimes({});
        setHasError(false);
        setIsModalShowing(false);
    };

    const handleSaveModal = (): void => {
        if (
            !selectedOptimalTimes ||
            Object.keys(selectedOptimalTimes).length < getSourceKeysFromSelectedSources(selectedSources).length
        ) {
            setHasError(true);
        } else {
            setHasError(false);

            const sourceToPublishTime: any = {};
            const selectedDateClone = new Date(selectedDate.getTime());

            _.forOwn(selectedOptimalTimes, (value, key) => {
                const date = mergeDateAndTimeString(selectedDateClone, value);

                sourceToPublishTime[key] = convertDateTimeToCalendarDate(DateTime.fromJSDate(date));
            });

            dispatch({
                type: 'set-source-to-publish-time',
                payload: {
                    value: sourceToPublishTime,
                },
            });

            setIsModalShowing(false);
        }
    };

    const mergeDateAndTimeString = (date: Date, time: string): Date => {
        const index = time.indexOf(':');
        const index2 = time.indexOf(' ');

        let hours = Number(time.substring(0, index));
        const minutes = Number(time.substring(index + 1, index2));

        const meridiem = time.substring(index2 + 1, time.length);

        if (meridiem.toLowerCase() === 'pm' && hours !== 12) {
            hours += 12;
        }

        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(0);

        return date;
    };

    const loadOptimalTimes = useCallback(async () => {
        const sources: any = [];

        const promises: any = [];

        Object.keys(selectedSources).forEach((sourceKey) => {
            let source = sourceKey;

            if (selectedSources[sourceKey]) {
                if ((sourceKey === 'IG_REELS' || sourceKey === 'IG_STORIES') && !sources.includes('INSTAGRAM')) {
                    source = 'INSTAGRAM';
                }

                const search: any = {
                    start: 0,
                    filters: [
                        {
                            name: '__publish_time_rank__',
                            values: ['4'],
                        },
                        {
                            name: '__publish_time_day_of_week__',
                            values: [DayOfWeek[selectedDate.getDay()]],
                        },
                        {
                            name: '__s_source__',
                            values: [source],
                        },
                    ],
                    count: 10,
                };

                sources.push(source);
                promises.push(fetchOptimalPublishTimes(tenantId, search));
            }
        });

        try {
            const allResponse = await trackPromise(Promise.all(promises), OVERLAY_AREA);
            const newOptimalTimes: any = [];

            forEach(allResponse, (responseItem: any, responseIndex: number) => {
                if (responseItem && responseItem.publishTime && responseItem.publishTime.timeToRank) {
                    // sort by values (the scores). This creates an array or arrays.
                    const sortedTimes = Object.entries(responseItem.publishTime.timeToRank).sort(
                        (a: any, b: any) => b[1] - a[1],
                    );

                    let validTimes: any = [];

                    // if selected date is today, remove times that are in the past
                    if (selectedDate.toDateString() === new Date().toDateString()) {
                        const currentHour = new Date().getHours();

                        _.forEach(sortedTimes, (time) => {
                            if (parseInt(time[0], 10) > currentHour) {
                                validTimes.push(time);
                            }
                        });
                    } else {
                        validTimes = _.clone(sortedTimes);
                    }

                    // grab the top 3
                    validTimes = validTimes.slice(0, 3);

                    // sort by keys (the times)
                    validTimes = validTimes.sort((a: any, b: any) => a[0] - b[0]);

                    // convert to regular time, and add to an array
                    const timesToDisplay: string[] = [];

                    _.forEach(validTimes, (time) => {
                        timesToDisplay.push(MilitaryTime[time[0]]);
                    });
                    newOptimalTimes.push({
                        source: sources[responseIndex],
                        optimalTimes: timesToDisplay,
                    });
                }
            });

            setOptimalTimes(newOptimalTimes);
        } catch (e) {
            console.log(e);
        }
    }, [selectedDate, selectedSources, tenantId]);

    useEffect(() => {
        if (sourceToPublishTime) {
            const newOptimalTimes = { ...selectedOptimalTimes };

            _.forOwn(sourceToPublishTime, (value, key) => {
                const dateTime = convertCalendarDateToDateTime(value);
                const date = dateTime.toJSDate();

                setSelectedDate(date);

                newOptimalTimes[key] = date
                    .toLocaleString('en-US', {
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                    })
                    .toLowerCase();
            });

            setSelectedOptimalTimes(newOptimalTimes);
        }
    }, [sourceToPublishTime]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadOptimalTimes();
    }, [loadOptimalTimes]);

    const onDateChange = (date: Date): void => {
        setSelectedDate(date);
    };

    const getSourcesWithNoOptimalTimes = (): string[] => {
        const sources: string[] = [];

        Object.keys(selectedSources).forEach((sourceKey) => {
            let source = sourceKey;

            if (selectedSources[sourceKey]) {
                if (source === 'IG_REELS' || source === 'IG_STORIES') {
                    source = 'INSTAGRAM';
                }
                const exists = _.some(optimalTimes, (optimalTime) => {
                    return optimalTime.source === source;
                });

                if (!exists) {
                    sources.push(source);
                }
            }
        });

        return sources;
    };

    const getTimeOptions = (): any => {
        const options: any = [];

        _.forOwn(MilitaryTime, (value, key) => {
            // if selected date is today, only include times that are after now.
            if (selectedDate.toDateString() === new Date().toDateString()) {
                const currentHour = new Date().getHours();

                if (parseInt(key, 10) > currentHour) {
                    options.push({
                        label: value,
                        value,
                    });
                }
            } else {
                options.push({
                    label: value,
                    value,
                });
            }
        });

        return options;
    };

    const createOptionFromTime = (time: string): any => {
        return {
            label: time,
            value: time,
        };
    };

    return (
        <div className="r4e-social-date-time-picker r4e-social-optimal-times-picker">
            {publishType === 'OPTIMAL_TIMES' && getSourceKeysFromSelectedSources(selectedSources).length > 0 && (
                <Button
                    onClick={(): void => {
                        handleShowModal();
                    }}
                    variant={ButtonVariant.Link}
                    className={classnames('date-time-button')}
                >
                    <i className="icon-calendar" />
                    <span className="data-time-button-text">{t('OPTIMAL_TIMES')}</span>
                </Button>
            )}

            {publishType === 'OPTIMAL_TIMES' && getSourceKeysFromSelectedSources(selectedSources).length === 0 && (
                <div className="right10 flex-center bold grey3">{t('OPTIMAL_TIMES_SELECT_SOURCES')}</div>
            )}

            <Modal
                show={isModalShowing}
                onHide={handleCloseModal}
                className="r4e-social-optimal-times-picker-modal modal-md"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('OPTIMAL_TIMES')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {hasError && (
                        <div className="alert alert-danger inline-block">
                            <i className="icon icon-exclamation-circle right5" />
                            {t('OPTIMAL_TIMES_REQUIRED_FIELDS')}
                        </div>
                    )}

                    <div className="top10">
                        <h5>{t('DATE')}</h5>
                        <DatePickerWithLocale
                            selectedDate={selectedDate}
                            minDate={new Date()}
                            value={selectedDate.toLocaleDateString('en-US')}
                            onChange={onDateChange}
                        />
                    </div>

                    <Overlay area={OVERLAY_AREA} />

                    <div className="top30">
                        <h5>{t('OPTIMAL_TIMES')}</h5>
                        <h6 className="regular grey1 bottom20">{t('CHOOSE_OPTIMAL_TIMES')}</h6>

                        {optimalTimes.map((item: any) => {
                            return (
                                <div className="bottom20">
                                    <div>
                                        <i className={`social-icon ${item.source.toLowerCase()}`} />
                                        {item.source.charAt(0).toUpperCase() + item.source.slice(1).toLowerCase()}
                                    </div>
                                    {item.optimalTimes.map((optimalTime: any) => {
                                        return (
                                            <Button
                                                type="submit"
                                                className={`right5 ${
                                                    selectedOptimalTimes[item.source] === optimalTime && 'selected-time'
                                                }`}
                                                variant="default"
                                                value={optimalTime}
                                                onClick={(e: any): void => {
                                                    const newOptimalTimes = { ...selectedOptimalTimes };

                                                    newOptimalTimes[item.source] = e.target.value;
                                                    setSelectedOptimalTimes(newOptimalTimes);
                                                }}
                                            >
                                                {optimalTime}
                                            </Button>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        {getSourcesWithNoOptimalTimes().map((source: any) => {
                            return (
                                <div className="bottom20">
                                    <div>
                                        <i className={`social-icon ${source.toLowerCase()}`} />
                                        {source.charAt(0).toUpperCase() + source.slice(1).toLowerCase()}
                                    </div>

                                    <h6 className="regular grey1">{t('NO_OPTIMAL_TIMES')}</h6>

                                    <Select
                                        options={getTimeOptions()}
                                        onChange={(selectedTimeOption: any): void => {
                                            const newOptimalTimes = { ...selectedOptimalTimes };

                                            newOptimalTimes[source] = selectedTimeOption.value;
                                            setSelectedOptimalTimes(newOptimalTimes);
                                        }}
                                        value={
                                            selectedOptimalTimes &&
                                            selectedOptimalTimes[source] &&
                                            createOptionFromTime(selectedOptimalTimes[source])
                                        }
                                        className="width200"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant={ButtonVariant.Secondary} onClick={handleCloseModal}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant={ButtonVariant.Primary} onClick={handleSaveModal}>
                        {t('SAVE')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
